import { endOfToday, startOfDay, subWeeks } from 'date-fns';
import { all, debounce, put, select, takeLatest } from 'redux-saga/effects';

import { DEBOUNCE_TIME } from 'constants/Global';
import type { SimpleRequestConfig } from 'modules/app/types';
import { simpleRequestsLoader } from 'modules/app/utils';
import { getDealerTimeZoneOffsetByInterventionId, getInterventionMode } from 'modules/interventions/selectors/details';
import { convertTimeZones } from 'utils/date';

import * as invoiceActions from '../actions';
import * as types from '../actionTypes';
import { getInvoiceParams, getModalInterventionId } from '../selectors';
import type { OpenModalAction } from '../types';

const simpleRequests: Record<string, SimpleRequestConfig> = {
  [types.FETCH_INVOICE_REQUEST]: {},
  [types.IMPORT_INVOICE_REQUEST]: {
    notifications: {
      success: {
        id: 'invoices.import.success',
        defaultMessage: 'Invoice imported successfully.'
      },
      failure: {
        id: 'invoices.import.failure',
        defaultMessage: 'An error occurred while importing the invoice.'
      }
    }
  }
};

function* initFetchInvoice(action: OpenModalAction) {
  const interventionId = action.payload?.interventionId;
  if (!interventionId) {
    return;
  }

  // Get timezone for date conversion
  const timeZone: string = yield select(state => getDealerTimeZoneOffsetByInterventionId(state, interventionId));
  const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Initialize filters with default values
  const defaultFrom = convertTimeZones(startOfDay(subWeeks(new Date(), 2)), timeZone, systemTimeZone).toISOString();
  const defaultTo = convertTimeZones(endOfToday(), timeZone, systemTimeZone).toISOString();

  yield put(
    invoiceActions.setInvoiceFilters([
      { field: 'filterByCustomer', value: true },
      { field: 'from', value: defaultFrom },
      { field: 'to', value: defaultTo },
      { field: 'search', value: null }
    ])
  );
}

// Refresh invoices when filters change
const onFilterChange = ({ type }: { type: string }) =>
  [
    types.SET_INVOICE_FILTER,
    types.SET_INVOICE_FILTERS,
    types.CLEAR_INVOICE_FILTERS,
    types.REMOVE_INVOICE_FILTER
  ].includes(type);

function* refreshInvoices() {
  const interventionId: string | null = yield select(getModalInterventionId);
  // @ts-expect-error - selectors are defined in JS and have different signatures
  const params = yield select(state => getInvoiceParams(state, interventionId));
  // @ts-expect-error - selector is defined in JS and has different signature
  const mode = yield select(state => getInterventionMode(state, interventionId));

  yield put(invoiceActions.fetchInvoice(params, mode));
}

export default function* InvoiceSagas() {
  yield all(simpleRequestsLoader(simpleRequests));

  yield takeLatest(types.OPEN_INVOICE_MODAL, initFetchInvoice);
  yield debounce(DEBOUNCE_TIME, onFilterChange, refreshInvoices);
}
